// Overrides for an account engagement model, on their own tab.
//
// They shared Fine tuning with the thresholds while thresholds had a page;
// the thresholds now sit on Event weights, beside the weights that move the
// score they cut, so the rules are what is left here: the fit override list,
// coloured in the account model's segments, and what the score's segments
// become once the rules apply. `OverridesPage` stays mounted for a tenant
// without the revamp.
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Card } from 'react-bootstrap';
import { Prompt } from 'react-router-dom';

import { Rule } from '../../../models/rules';
import Store from '../../../store';
import Error from '../../components/error/Error';
import Spinner from '../../components/Spinner';
import ModelPerformanceBands from '../../components/model/ModelPerformanceBands';
import OverrideRules from '../../components/model/OverrideRules';
import { unitOf } from '../../components/model/segmentVocabulary';
import './fineTuning.css';

interface Props {
  store?: Store;
}

interface State {
  isLoading: boolean;
  loadError: string | null;
  hasUnsavedRule: boolean;
}

class EngagementOverridesPage extends React.Component<Props, State> {
  mounted = false;

  constructor(props: Props) {
    super(props);
    this.state = { isLoading: true, loadError: null, hasUnsavedRule: false };
    this.saveRules = this.saveRules.bind(this);
  }

  async componentDidMount() {
    this.mounted = true;
    const { store } = this.props;
    const { tenant, modelId } = store;
    try {
      await Promise.all([
        store.fetchFormFields(),
        store.overridesPage.get({ tenant, modelId }),
        store.loadOverrideEnsembleData().catch(() => null),
      ]);
    } catch (error) {
      if (this.mounted)
        this.setState({ loadError: error?.message ?? String(error) });
    } finally {
      if (this.mounted) this.setState({ isLoading: false });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  async saveRules(): Promise<boolean> {
    const { store } = this.props;
    const { tenant, modelId } = store;
    store.analyticsTrackEvent('Updated override');
    await store.saveOverrideRules();
    if (store.overridesPage.savingError) return false;
    await Promise.all([
      store.overridesPage.get({ tenant, modelId }),
      store.loadOverrideEnsembleData().catch(() => null),
    ]);
    return true;
  }

  render() {
    const { overridesPage, formFields, activeModel, isAllowedToEdit } =
      this.props.store;
    if (this.state.isLoading) return <Spinner />;
    if (this.state.loadError) return <Error message={this.state.loadError} />;
    const withOverrides =
      overridesPage.ensemble?.performances?.performances ?? null;

    return (
      <div className="ftune mt-3 mb-5">
        <Prompt
          when={this.state.hasUnsavedRule}
          message="A rule is edited and not saved. Leave anyway?"
        />
        <Card className="ftune-card">
          <Card.Body>
            <h5 className="ftune-title">Overrides</h5>
            <p className="ftune-hint">
              Business rules on top of the score, forcing the segment of
              accounts by their activity. Applied top to bottom.
            </p>
            <OverrideRules
              model={overridesPage}
              formFields={formFields ?? []}
              updateRules={(rules: Rule[]) =>
                void this.props.store.updateOverrideRules(rules)
              }
              saveRules={this.saveRules}
              onDirtyChange={(dirty) =>
                this.setState({ hasUnsavedRule: dirty })
              }
              modelType={activeModel.type}
              isAllowedToEdit={isAllowedToEdit}
            />
          </Card.Body>
        </Card>
        {withOverrides && (
          <div className="ftune-card">
            <ModelPerformanceBands
              performances={withOverrides}
              vocabulary="behavioral"
              unit={unitOf(activeModel.type)}
              title="With overrides"
              scope="The score's segments after the rules above"
            />
          </div>
        )}
      </div>
    );
  }
}

export default inject('store')(observer(EngagementOverridesPage));
